'use client'

import { useMemo, useState } from 'react'
import { mtc } from '@/styles'
import { Select } from '@/ui'

export interface SearchableSelectOption<T extends string> {
  value: T | number
  label: string
  key?: string
  onClick?: () => void
  isTaxCompliant?: boolean
}

export interface SearchableSelectProps<T extends string> {
  options: SearchableSelectOption<T>[]
  value: T | undefined
  onChange: (value: T | undefined) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  onBlur?: () => void
  variant?: 'default' | 'text'
  inputRef?: React.RefObject<HTMLInputElement>
}

export const SearchableSelect = <T extends string>({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  disabled,
  className,
  onBlur,
  inputRef,
}: SearchableSelectProps<T>) => {
  const [searchValue, setSearchValue] = useState('')
  const [open, setOpen] = useState(false)
  const filteredOptions = useMemo(() => {
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [options, searchValue])

  const selected = value
    ? options.find((option) => Number(option.value) === Number(value))
    : undefined

  return (
    <Select
      open={open}
      disabled={disabled}
      onBlur={onBlur}
      value={value ?? ''}
      searchable
    >
      <Select.Trigger
        onClick={() => setOpen((prev) => !prev)}
        className={mtc(
          'searchable-select-trigger min-h-[48px] h-auto justify-start',
          className
        )}
      >
        <div className="flex flex-1 ml-2 flex-wrap gap-y-2 items-center justify-start">
          <div
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            className="min-w-[100px] flex-1"
          >
            <input
              placeholder={selected ? selected.label : placeholder}
              value={searchValue}
              ref={inputRef}
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                setSearchValue(e.target.value)
                setOpen(true)
              }}
              onClick={(e) => {
                e.currentTarget.focus()
                setOpen(true)
              }}
              onBlur={(e) => {
                if (
                  e.relatedTarget?.classList.contains(
                    'searchable-select-content'
                  ) ||
                  e.relatedTarget?.classList.contains(
                    'searchable-select-option'
                  ) ||
                  e.relatedTarget?.classList.contains(
                    'searchable-select-trigger'
                  )
                ) {
                  e.currentTarget.focus()
                } else {
                  setSearchValue('')
                  onBlur?.()
                }
              }}
              className={`w-full h-[30px] outline-none text-sm bg-transparent text-left ${
                selected ? 'placeholder-neutral-900' : 'placeholder-neutral-400'
              }`}
            />
          </div>
        </div>
      </Select.Trigger>

      <Select.Content
        onPointerDownOutside={() => setOpen(false)}
        className="searchable-select-content"
      >
        {filteredOptions.length === 0 ? (
          <div className="text-sm text-neutral-500 px-4 py-2">
            No options found
          </div>
        ) : (
          filteredOptions.map(({ key, value: v, label, onClick }) => (
            <Select.Item
              key={key || v}
              value={String(v)}
              className="searchable-select-option"
              onClick={() => {
                onChange(v as T)
                setOpen(false)
                onClick?.()
                setTimeout(() => {
                  setSearchValue('')
                }, 100)
              }}
            >
              {label}
            </Select.Item>
          ))
        )}
      </Select.Content>
    </Select>
  )
}
